import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './staff-design-system.css';
import './Staff.css';
import { OpenTableDetailSheet } from '../../components/staff/OpenTableDetailSheet';
import { TableCard } from '../../components/staff/ui/TableCard';
import { Pill } from '../../components/staff/ui/Pill';
import { SectionHeader } from '../../components/staff/ui/SectionHeader';
import { StaffLoadError, type StaffLoadErrorKind } from '../../components/staff/StaffLoadError';
import { apiFetch } from '../../utils/api';

interface LiveTable {
  number: number;
  status: 'occupied' | 'paying';
  covers: number;
  totalTTC: number;
  amountPaid?: number;
  durationMin: number;
  duration: string;
  openedAt?: string;
  owner: string;
  nbItems: number;
  customer?: { name: string; avatarUrl?: string } | null;
  isLong: boolean;
}

type FilterKey = 'all' | 'occupied' | 'paying' | 'long';

export function StaffSalle() {
  const navigate = useNavigate();
  const [tables, setTables] = useState<LiveTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<StaffLoadErrorKind | null>(null);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [selectedTable, setSelectedTable] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await apiFetch(`/pos/dashboard-summary`);
        if (r.status === 401) { if (!cancelled) setError('auth'); return; }
        if (!r.ok) throw new Error(`${r.status}`);
        const json = await r.json() as { tables: LiveTable[] };
        if (!cancelled) {
          setTables(json.tables ?? []);
          setError(null);
        }
      } catch {
        if (!cancelled) setError('error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const counts = useMemo(() => ({
    all: tables.length,
    occupied: tables.filter(t => t.status === 'occupied').length,
    paying: tables.filter(t => t.status === 'paying').length,
    long: tables.filter(t => t.isLong).length,
  }), [tables]);

  const filtered = useMemo(() => {
    let list = tables;
    if (filter === 'occupied') list = list.filter(t => t.status === 'occupied');
    else if (filter === 'paying') list = list.filter(t => t.status === 'paying');
    else if (filter === 'long') list = list.filter(t => t.isLong);
    return list.sort((a, b) => {
      const rank = (t: LiveTable) => (t.isLong ? 0 : t.status === 'paying' ? 1 : 2);
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      return b.durationMin - a.durationMin;
    });
  }, [tables, filter]);

  const totalCovers = tables.reduce((s, t) => s + t.covers, 0);
  const totalCA = tables.reduce((s, t) => s + t.totalTTC, 0);

  return (
    <div className="staff-page-v2 staff-page">
      <div className="staff-header-v2">
        <button className="staff-back-v2" onClick={() => navigate('/staff/dashboard')}>‹</button>
        <h1>Salle</h1>
        <span className="staff-badge-v2">{tables.length} table{tables.length > 1 ? 's' : ''}</span>
      </div>

      {/* Summary inline */}
      <div className="staff-salle-summary-v2">
        <div>
          <strong>{totalCovers > 0 ? totalCovers : '—'}</strong>
          <span>Couverts</span>
        </div>
        <div>
          <strong>{totalCA.toFixed(0)} €</strong>
          <span>En cours</span>
        </div>
      </div>

      {/* Filtres pills */}
      <div className="staff-filters-row">
        <Pill active={filter === 'all'} count={counts.all} onClick={() => setFilter('all')}>Toutes</Pill>
        <Pill active={filter === 'occupied'} count={counts.occupied} onClick={() => setFilter('occupied')}>Occupées</Pill>
        <Pill active={filter === 'paying'} count={counts.paying} onClick={() => setFilter('paying')}>Paiement</Pill>
        <Pill active={filter === 'long'} count={counts.long} onClick={() => setFilter('long')}>Alertes</Pill>
      </div>

      {loading && <p className="staff-empty">Chargement…</p>}
      {error && <StaffLoadError kind={error} />}

      {!loading && !error && (
        <>
          {filtered.length > 0 ? (
            <div className="staff-tables-grid">
              {filtered.map((t) => (
                <TableCard
                  key={t.number}
                  number={t.number}
                  status={t.status}
                  isLong={t.isLong}
                  covers={t.covers}
                  totalTTC={t.totalTTC}
                  amountPaid={t.amountPaid}
                  duration={t.duration}
                  openedAt={t.openedAt}
                  owner={t.owner}
                  nbItems={t.nbItems}
                  customer={t.customer}
                  onClick={() => setSelectedTable(t.number)}
                />
              ))}
            </div>
          ) : (
            <div className="staff-empty">
              {filter === 'all' ? 'Aucune table ouverte' : 'Aucune table dans ce filtre'}
            </div>
          )}
        </>
      )}

      <OpenTableDetailSheet
        tableNumber={selectedTable}
        onClose={() => setSelectedTable(null)}
      />
    </div>
  );
}
